import React, { useState } from 'react';
import { Category } from '../utils/types';

interface CategoryItemProps {
    category: Category;
    onSelectCategory: (category: Category) => void;
    selectedCategories: Category[];
    allCategories: Category[];
    level: number;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
    category,
    onSelectCategory,
    selectedCategories,
    allCategories,
    level,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const paddingLeft = level * 15; // Adjust indentation as needed (e.g., 15px per level)

    // Find direct children of the current category
    const directChildren = allCategories
        .filter(cat => Number(cat.cat_parent) === category.id)
        .sort((a, b) => a.cat_name.localeCompare(b.cat_name));

    const hasChildren = directChildren.length > 0;

    // Check if the current category is selected
    const isSelected = selectedCategories.some(c => c.id === category.id);

    // Handler for selecting the category (clicking the name)
    const handleSelectCategory = () => {
        onSelectCategory(category);
    };

    // Handler for expanding/collapsing (clicking the arrow)
    const handleToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent the parent button from also triggering selection
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            <li
                key={category.id}
                className={`border-b border-b-1 border-b-gray-300
                    ${isSelected ? 'bg-orange-200  py-1 font-semibold text-blue-600 shadow-lg' : ''}`}
                style={{ paddingLeft: `${paddingLeft}px` }}
            >
                <button
                    onClick={handleSelectCategory} // This button now only selects the category
                    className={`flex-grow break-words m-0 px-2 py-2  text-left hover:bg-orange-300 hover:rounded hover:underline hover:underline-offset-8 decoration-orange-400`}
                >
                    {category.cat_name}&nbsp;
                    <span className='text-xs text-gray-400'>({category.cat_prod_qty})</span>
                </button>

                {hasChildren && (
                    <button
                        onClick={handleToggleExpand} // This button now only toggles expand/collapse
                        className={`px-2 py-2 mr-1 text-gray-600 hover:text-gray-900 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        aria-expanded={isExpanded}
                        aria-controls={`children-${category.id}`}
                    >
                        ▶
                    </button>
                )}
            </li>

            {hasChildren && isExpanded && (
                <ul id={`children-${category.id}`}>
                    {directChildren.map(child => (
                        <CategoryItem
                            key={child.id}
                            category={child}
                            onSelectCategory={onSelectCategory}
                            selectedCategories={selectedCategories}
                            allCategories={allCategories}
                            level={level + 1}
                        />
                    ))}
                </ul>
            )}
        </>
    );
};

export default CategoryItem;
